/**
 * NewsClimaticPanel — Buscador de noticias sobre fenómenos climáticos /
 * atmosféricos en una provincia o municipio, con rango de fechas.
 *
 * Mismo patrón visual que `MeteoPanel`:
 *   - Barra de búsqueda con dirección + fecha (rango opcional).
 *   - Chips de fenómenos sugeridos + cuadro de texto libre.
 *   - Lista de resultados con fuente, fecha, idioma, snippet y URL.
 *   - Botón "Generar PDF" que adjunta el informe al expediente si entras
 *     desde el contexto de uno.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Newspaper, MagnifyingGlass, FilePdf, ArrowSquareOut, X, CalendarBlank,
  MapPin, Globe,
} from "@phosphor-icons/react";

const MAX_RANGE_DAYS = 90;

function todayISO() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}
function diffDays(a, b) {
  return Math.abs((new Date(b) - new Date(a)) / 86400000);
}

export default function NewsClimaticPanel({
  defaultLocation = "",
  defaultStartDate = "",
  defaultEndDate = "",
  expedienteId = null,
  onAttached,
}) {
  const [location, setLocation] = useState(defaultLocation);
  const [startDate, setStartDate] = useState(defaultStartDate || "");
  const [endDate, setEndDate] = useState(defaultEndDate || "");
  const [rangeMode, setRangeMode] = useState(!!(defaultStartDate && defaultEndDate));
  const [phenomena, setPhenomena] = useState([]);
  const [selected, setSelected] = useState([]);
  const [freeText, setFreeText] = useState("");
  const [result, setResult] = useState(null);
  // URLs de noticias marcadas para incluir en el PDF (por defecto, todas).
  const [pickedUrls, setPickedUrls] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api.get("/news-climatic/phenomena").then((r) => {
      setPhenomena(r.data.items || []);
      // Pre-selección sensata
      setSelected((prev) => prev.length ? prev : ["tormenta", "granizo", "DANA", "temporal"]);
    }).catch(() => setPhenomena([]));
  }, []);

  // Auto-búsqueda al montar si llegamos ya con datos del expediente.
  useEffect(() => {
    if (defaultLocation && (defaultStartDate || todayISO())) {
      runSearch(defaultLocation, defaultStartDate, defaultEndDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultLocation, defaultStartDate, defaultEndDate]);

  const toggleChip = (k) => {
    setSelected((prev) => prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]);
  };

  const runSearch = async (loc = location, s = startDate, e = (rangeMode ? endDate : "")) => {
    const locTrim = (loc || "").trim();
    if (locTrim.length < 2) { toast.error("Indica la provincia o municipio"); return; }
    if (e && s && new Date(e) < new Date(s)) {
      toast.error("La fecha fin no puede ser anterior a la inicio"); return;
    }
    if (e && s && diffDays(s, e) > MAX_RANGE_DAYS) {
      toast.error(`Ventana máxima ${MAX_RANGE_DAYS} días`); return;
    }
    setLoading(true);
    try {
      const params = {
        location: locTrim,
        keywords: selected.join(","),
        limit: 60,
      };
      if (freeText.trim()) params.free_text = freeText.trim();
      if (s) params.start_date = s;
      if (e) params.end_date = e;
      const r = await api.get("/news-climatic/search", { params });
      setResult(r.data);
      // Por defecto, todas las noticias quedan seleccionadas para el PDF.
      const allUrls = (r.data?.items || [])
        .map((it) => it.url)
        .filter(Boolean);
      setPickedUrls(new Set(allUrls));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo realizar la búsqueda");
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const onGeneratePdf = async () => {
    if (!result) { toast.error("Primero realiza una búsqueda"); return; }
    const itemsAll = result.items || [];
    const itemsPicked = itemsAll.filter((it) => pickedUrls.has(it.url));
    if (itemsPicked.length === 0) {
      toast.error("Selecciona al menos una noticia para incluir en el PDF");
      return;
    }
    setGenerating(true);
    try {
      const body = {
        location: location.trim(),
        keywords: selected,
        free_text: freeText.trim() || null,
        start_date: startDate || null,
        end_date: (rangeMode && endDate) ? endDate : null,
        limit: 100,
        expediente_id: expedienteId || null,
        items: itemsPicked,
      };
      const r = await api.post("/news-climatic/report", body, { responseType: "blob" });
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : `noticias_climaticas_${todayISO()}.pdf`;
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);

      const attId = r.headers["x-attachment-id"];
      if (expedienteId && attId) {
        toast.success("Informe de noticias generado y adjuntado al expediente");
        onAttached?.(attId);
      } else {
        toast.success("Informe de noticias descargado");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error generando el informe");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="news-climatic-panel">
      <form
        onSubmit={(e) => { e.preventDefault(); runSearch(); }}
        className="bg-white border border-border rounded-lg p-4 space-y-3"
      >
        <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_140px_auto] gap-2 items-end">
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-bold text-muted block mb-1">
              <MapPin className="inline w-3 h-3 mr-1" weight="duotone" />Provincia o municipio
            </span>
            <input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Ej. Valencia, Sevilla, Lleida…"
              data-testid="news-location"
              className="w-full text-sm border border-border rounded-md px-3 py-2"
            />
          </label>
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-bold text-muted block mb-1">
              <CalendarBlank className="inline w-3 h-3 mr-1" weight="duotone" />Desde
            </span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              max={todayISO()}
              data-testid="news-start"
              className="w-full text-sm border border-border rounded-md px-3 py-2"
            />
          </label>
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-bold text-muted block mb-1">
              <CalendarBlank className="inline w-3 h-3 mr-1" weight="duotone" />Hasta
            </span>
            <input
              type="date"
              value={rangeMode ? endDate : ""}
              onChange={(e) => { setEndDate(e.target.value); setRangeMode(!!e.target.value); }}
              min={startDate || undefined}
              max={todayISO()}
              data-testid="news-end"
              className="w-full text-sm border border-border rounded-md px-3 py-2"
            />
          </label>
          <button
            type="submit"
            disabled={loading}
            data-testid="news-search-btn"
            className="bg-brand hover:bg-brand/90 text-white text-sm font-semibold px-4 h-[38px] rounded-md inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <MagnifyingGlass className="w-4 h-4" weight="bold" />
            {loading ? "Buscando…" : "Buscar"}
          </button>
        </div>

        <div>
          <span className="text-[10.5px] uppercase tracking-wider font-bold text-muted block mb-1.5">
            Fenómenos a buscar
          </span>
          <div className="flex flex-wrap gap-1.5" data-testid="news-chips">
            {phenomena.map((p) => {
              const active = selected.includes(p);
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => toggleChip(p)}
                  data-testid={`news-chip-${p}`}
                  className={`text-[11.5px] font-medium px-2.5 py-1 rounded-full border transition-colors ${
                    active
                      ? "bg-brand text-white border-brand"
                      : "bg-white text-foreground border-border hover:border-brand"
                  }`}
                >
                  {active && <span className="mr-1">✓</span>}{p}
                </button>
              );
            })}
          </div>
        </div>

        <label className="block">
          <span className="text-[10.5px] uppercase tracking-wider font-bold text-muted block mb-1">
            Términos adicionales (opcional)
          </span>
          <input
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            placeholder='Ej. "vehículo dañado" OR "techo roto"'
            data-testid="news-free-text"
            className="w-full text-sm border border-border rounded-md px-3 py-2"
          />
        </label>
      </form>

      {/* Resultados */}
      {loading && (
        <div className="bg-white border border-border rounded-lg p-6 text-center text-muted text-sm">
          Buscando noticias en Google News…
        </div>
      )}

      {!loading && result && (
        <div className="bg-white border border-border rounded-lg overflow-hidden">
          <header className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-b border-border">
            <div className="flex items-center gap-2 min-w-0">
              <Newspaper className="w-4 h-4 text-brand" weight="duotone" />
              <h3 className="font-display font-bold text-sm">
                Resultados <span className="text-muted font-medium">({result.items?.length || 0})</span>
              </h3>
              {(result.items?.length || 0) > 0 && (
                <span
                  className="text-[11.5px] text-muted ml-1"
                  data-testid="news-picked-count"
                >
                  · {pickedUrls.size} seleccionada{pickedUrls.size === 1 ? "" : "s"}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {(result.items?.length || 0) > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      const allUrls = (result.items || []).map((it) => it.url).filter(Boolean);
                      setPickedUrls(new Set(allUrls));
                    }}
                    data-testid="news-select-all-btn"
                    className="text-[11.5px] font-semibold px-2.5 py-1.5 rounded-md border border-border hover:border-brand hover:text-brand"
                  >
                    Seleccionar todo
                  </button>
                  <button
                    type="button"
                    onClick={() => setPickedUrls(new Set())}
                    data-testid="news-select-none-btn"
                    className="text-[11.5px] font-semibold px-2.5 py-1.5 rounded-md border border-border hover:border-brand hover:text-brand"
                  >
                    Ninguna
                  </button>
                </>
              )}
              <button
                onClick={onGeneratePdf}
                disabled={generating || !result.items?.length || pickedUrls.size === 0}
                data-testid="news-pdf-btn"
                className="text-[12px] font-semibold px-3 py-1.5 rounded-md bg-foreground text-white hover:bg-foreground/90 inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <FilePdf className="w-4 h-4" weight="duotone" />
                {generating
                  ? "Generando…"
                  : `${expedienteId ? "PDF + adjuntar" : "Descargar PDF"} (${pickedUrls.size})`}
              </button>
            </div>
          </header>

          {(result.items?.length || 0) === 0 && (
            <div className="px-4 py-8 text-center text-muted text-sm italic">
              No se han encontrado noticias para esos criterios.
            </div>
          )}

          <ul className="divide-y divide-border" data-testid="news-results">
            {(result.items || []).map((n, idx) => (
              <NewsItem
                key={`${n.url}-${idx}`}
                n={n}
                idx={idx + 1}
                checked={pickedUrls.has(n.url)}
                onToggle={() => {
                  setPickedUrls((prev) => {
                    const next = new Set(prev);
                    if (next.has(n.url)) next.delete(n.url);
                    else next.add(n.url);
                    return next;
                  });
                }}
              />
            ))}
          </ul>
        </div>
      )}

      {!loading && !result && (
        <div className="bg-white border border-dashed border-border rounded-lg p-8 text-center text-muted text-sm">
          Indica una provincia y los fenómenos a buscar para encontrar noticias
          climáticas o atmosféricas en Google News.
        </div>
      )}
    </div>
  );
}

function NewsItem({ n, idx, checked, onToggle }) {
  return (
    <li
      className={`px-4 py-3 hover:bg-zinc-50 transition-colors ${checked ? "" : "opacity-60"}`}
      data-testid={`news-item-${idx}`}
    >
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          aria-label={`Incluir noticia ${idx} en el PDF`}
          data-testid={`news-check-${idx}`}
          className="mt-1 w-4 h-4 accent-[#E85D2C] cursor-pointer flex-shrink-0"
        />
        <span className="w-6 h-6 rounded-md bg-brand/10 border border-brand/20 text-brand text-[11px] font-bold flex items-center justify-center flex-shrink-0">
          {idx}
        </span>
        <div className="min-w-0 flex-1">
          <a
            href={n.url}
            target="_blank"
            rel="noreferrer noopener"
            className="text-[13.5px] font-semibold text-foreground hover:text-brand break-words inline-flex items-start gap-1.5"
            data-testid={`news-link-${idx}`}
          >
            <span>{n.title}</span>
            <ArrowSquareOut className="w-3 h-3 mt-1 flex-shrink-0 text-muted" weight="bold" />
          </a>
          <div className="text-[11px] text-muted mt-1 flex items-center gap-2 flex-wrap">
            <span className="font-semibold">{n.source}</span>
            {n.published_at && (
              <>
                <span>·</span>
                <span>{n.published_at.slice(0, 10)}</span>
              </>
            )}
            <span>·</span>
            <span className="inline-flex items-center gap-1">
              <Globe className="w-2.5 h-2.5" weight="bold" />
              {(n.language || "es").toUpperCase()}
            </span>
          </div>
          {n.snippet && (
            <p className="text-[12px] text-foreground/80 mt-1.5 leading-snug line-clamp-3">
              {n.snippet}
            </p>
          )}
        </div>
      </div>
    </li>
  );
}
